import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCreateTenant } from "~/lib/tenants";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;

function suggestSlug(name: string): string {
    return name
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

export function CreateTenantDialog(): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [slugDirty, setSlugDirty] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = useCreateTenant();

    function reset(): void {
        setName("");
        setSlug("");
        setSlugDirty(false);
        setError(null);
        create.reset();
    }

    function onNameChange(v: string): void {
        setName(v);
        if (!slugDirty) setSlug(suggestSlug(v));
    }

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        const trimmedName = name.trim();
        const trimmedSlug = slug.trim().toLowerCase();
        if (!trimmedName) {
            setError("Name required.");
            return;
        }
        if (!SLUG_RE.test(trimmedSlug)) {
            setError("Slug must be 2-64 chars, [a-z0-9-], starting with a letter or digit.");
            return;
        }
        try {
            const res = await create.mutateAsync({ slug: trimmedSlug, name: trimmedName });
            toast.success(`Tenant “${res.tenant.name}” created`);
            setOpen(false);
            reset();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to create tenant.");
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className="btn btn--primary press-crush">
                    <Plus size={14} />
                    <span>New tenant</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                <Dialog.Content
                    className="dialog-content enter-up"
                    style={{ padding: "var(--space-6)" }}
                >
                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                        <Dialog.Title className="type-h2">New tenant</Dialog.Title>
                        <Dialog.Close asChild>
                            <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                <X size={14} />
                            </button>
                        </Dialog.Close>
                    </div>
                    <Dialog.Description asChild>
                        <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Name</span>
                                <input
                                    autoFocus
                                    required
                                    className="input"
                                    value={name}
                                    onChange={(e) => onNameChange(e.target.value)}
                                    placeholder="Acme Corporation"
                                />
                            </label>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">
                                    Slug <span className="text-text-subtle">(URL-safe, lowercase)</span>
                                </span>
                                <input
                                    required
                                    pattern="[a-z0-9][a-z0-9-]{1,63}"
                                    className="input"
                                    value={slug}
                                    onChange={(e) => {
                                        setSlug(e.target.value);
                                        setSlugDirty(true);
                                    }}
                                    placeholder="acme"
                                    style={{ fontFamily: "var(--font-family-mono)" }}
                                />
                            </label>

                            {error && (
                                <div
                                    className="chip chip--danger shake"
                                    style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}

                            <button type="submit" className="btn btn--primary press-crush" disabled={create.isPending}>
                                {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                <span>{create.isPending ? "Creating…" : "Create tenant"}</span>
                            </button>
                        </form>
                    </Dialog.Description>
                </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
